import { useQuery } from '@tanstack/react-query';

import { apiFetch, ApiRequestError } from '@/lib/api/client';
import { runtimeConfig } from '@/lib/config';
import { queryKeys } from '@/lib/queryKeys';

/**
 * 직원찾기(사내 주소록)의 데이터 접근.
 *
 * 인력 목록(`/v1/personnel`)을 쓰지 않는다 — 그쪽은 인사 메뉴 권한이 있어야 열리고 생년월일·
 * 기술등급까지 함께 나간다. 주소록은 전 직원이 여는 도구라 <b>필드를 줄인 별도 통로</b>를 쓴다
 * (`DirectoryController`).
 *
 * 트리와 명함을 나눠 읽는 이유: 트리는 전 직원의 이름표라 한 번에 크고, 사진·내선·오늘 일정은
 * 고른 한 사람에게만 필요하다.
 *
 * **휴대전화만은 예외로 트리에 실린다** (사용자 지시 2026-09-23 — 번호로 직원을 찾는다).
 * 브라우저에서 대조하려면 값이 여기 있어야 하고, 그래서 주소록을 여는 사람은 전 직원의 번호를
 * 함께 받는다. 화면에 그리지는 않는다 — 대조에만 쓴다.
 */

export type DirectoryMember = {
  personnelId: string;
  name: string;
  rank: string | null;
  position: string | null;
  /**
   * 휴대전화 — <b>찾기 위해서만</b> 온다(사용자 지시 2026-09-23: 번호로 직원을 찾는다).
   *
   * <p>트리는 이름표를 그리는 자리라 이 값을 <b>화면에 그리지 않는다</b>. 번호를 보는 자리는
   * 여전히 명함({@link DirectoryPerson})이다 — 목록에 깔아 두면 전 직원의 번호가 스크롤 한 번에
   * 읽히고, 그건 「찾을 수 있다」 와 다른 이야기다.
   */
  phone: string | null;
  teamLead: boolean;
  /**
   * 증명사진이 등록돼 있는가 — <b>링크가 아니다</b>(사용자 결정 2026-09-29).
   *
   * <p>주소는 {@link directoryPhotoUrl} 가 id 로 만든다. 그래서 이 트리에는 사람 수만큼의 긴
   * presigned 링크가 실리지 않고, 만료도 없다(그쪽은 10분이고 이 응답은 그보다 오래 캐시된다).
   *
   * <p>false 인 줄에는 사진을 <b>부르지 않는다</b>. 없는 사람에게도 주소를 물리면 줄마다 404 가
   * 한 번씩 나가고, 그 사이 이니셜 자리가 깜빡인다.
   */
  hasPhoto: boolean;
  /**
   * 이메일 — 휴대전화처럼 <b>찾기 위해서만</b> 온다(사용자 지시 2026-10-14). 트리에 그리지 않는다 —
   * 보는 자리는 명함이다. 서버가 재시작 전이면 없다.
   */
  email?: string | null;
  /**
   * 담당업무(V437 · 사용자 지시 2026-10-14). 검색칸이 이름과 함께 이 글에서도 찾는다 — 따로 켜는
   * 「업무로 찾기」 를 두지 않는다. 서버가 재시작 전이면 없다.
   */
  duties?: string | null;  /** 전결표(V443)에서 주 업무자로 이어진 업무들. 깔린 앱이 읽어 서버가 남겨 둔다. 서버가 재시작 전이면 없다. */
  matrixDuties?: string[];
  /** 업무분장(V599)에서 담당자로 이어진 업무들 — 검색칸이 전결표 대신 이것으로 찾는다(2026-10-30). 서버가 재시작 전이면 없다. */
  assignedDuties?: string[];
  /** 학력(V615) — 공개로 둔 사람만 실린다(「OO대학교 건축공학과 (대졸)」). 검색칸이 학교·전공으로도 찾는다. 서버가 재시작 전이면 없다. */
  educations?: string[];
};

/**
 * 증명사진 바이트를 읽는 주소 — 같은 오리진으로 중계한다({@code /v1/directory/{id}/photo}).
 *
 * <p>presigned 링크를 트리에 싣지 않은 이유가 여기 들어 있다: 이 주소는 <b>만료되지 않아</b>
 * 브라우저 캐시가 살고, 저장소 오리진이 화면에 나가지 않는다. 늦게 와도 괜찮은 그림이라
 * 부르는 쪽은 {@code loading="lazy"} 로 걸어 둔다 — 보이는 줄만 받는다.
 *
 * <p>앱도 같은 주소를 쓴다. 다만 인증이 달라 <b>헤더를 직접 실어야</b> 한다 — 웹은 세션 쿠키가
 * 저절로 따라가지만 앱은 Bearer 라, {@code PersonAvatar} 가 {@code source.headers} 로 토큰을 함께
 * 보낸다(mobile/src/ui/PersonAvatar.tsx).
 */
export function directoryPhotoUrl(personnelId: string): string {
  return `${runtimeConfig().apiBaseUrl}/v1/directory/${personnelId}/photo`;
}

export type DirectoryUnit = {
  /** DEPARTMENT | SITE */
  unitType: string;
  unitName: string;
  members: DirectoryMember[];
};

export type DirectoryScheduleItem = {
  /** leave | absence | meeting */
  kind: string;
  title: string;
  /** `HH:MM`. 종일 일정·연차는 null. */
  time: string | null;
  detail: string | null;
};

export type DirectorySchedule = {
  date: string;
  /** LEAVE | ABSENCE | MEETING | WORKING */
  status: string;
  statusLabel: string;
  items: DirectoryScheduleItem[];
};

export type DirectoryPerson = {
  id: string;
  name: string;
  rank: string | null;
  position: string | null;
  field: string | null;
  deptSite: string | null;
  phone: string | null;
  extension: string | null;
  email: string | null;
  /** 만료되는 presigned URL. 없으면 화면이 이니셜 아바타를 그린다. */
  photoUrl: string | null;
  /** 자리배치도에 앉은 층(「3층」). 서버가 재시작 전이면 없다. */
  floor?: string | null;
  units: Array<{ unitType: string; unitName: string; roleTitle: string | null; teamLead: boolean }>;
  today: DirectorySchedule;
  /** 담당업무(V437). 자유 글 한 칸. 서버가 재시작 전이면 없다. */
  duties?: string | null;
  /** 전결표(V443)에서 주 업무자로 이어진 업무들. 담당업무 글과 따로 온다. 서버가 재시작 전이면 없다. */
  matrixDuties?: string[];
  /** 업무분장(V599)에서 담당자로 이어진 업무들 — 명함의 「담당 업무」(2026-10-30 · 전결표 업무 대신). 서버가 재시작 전이면 없다. */
  assignedDuties?: string[];
  /** 학력(V615) — 공개로 둔 사람만 실린다(「OO대학교 건축공학과 (대졸)」). 검색칸이 학교·전공으로도 찾는다. 서버가 재시작 전이면 없다. */
  educations?: string[];
};

/**
 * 조직도 트리. 모달을 열 때마다 다시 읽지 않도록 오래 신선한 것으로 본다 —
 * 인사 배치는 하루에 몇 번씩 바뀌는 자료가 아니다.
 */
export function useDirectoryTree(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.directoryTree,
    queryFn: () => fetchTree(),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/**
 * 고른 사람의 명함. 오늘 상태가 함께 오므로 캐시를 짧게 잡는다 —
 * 회의가 끝났는데 "회의 있음" 이 남아 있으면 이 화면의 쓸모가 사라진다.
 */
export function useDirectoryPerson(personnelId: string | null) {
  return useQuery({
    queryKey: [...queryKeys.directoryPerson, personnelId],
    queryFn: () => apiFetch<DirectoryPerson>(`/v1/directory/${personnelId}`),
    enabled: personnelId !== null,
    staleTime: 60_000,
  });
}

/** 전결표 한 줄 — 서버 {@code DutyMatrixDtos.SearchHit}. 자리는 「담당자」·「팀장」·「본부장」·「대표이사」. */
export type DirectoryMatrixDuty = {
  dept: string;
  /** 「1. 공정관리 › 가. 계획」. 묶음 밖의 줄이면 null */
  section: string | null;
  task: string;
  drafters: string[];
  reviewers: string[];
  finalBy: string | null;
  /** △ — 중요한 건이면 그 자리까지 간다 */
  conditional: string[];
};

/** 명함의 「전결표 업무」 모달이 열릴 때만 받는다 — 명함마다 20~30줄을 미리 끌어오지 않는다. */
export function useDirectoryMatrixDuties(personnelId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...queryKeys.directoryMatrixDuties, personnelId],
    queryFn: () => apiFetch<DirectoryMatrixDuty[]>(`/v1/directory/${personnelId}/matrix-duties`),
    enabled,
    staleTime: 60_000,
  });
}

/** 업무분장 한 줄 — 서버 {@code DutyAssignmentDtos.PersonDuty}. 명함의 「담당 업무」 모달과 마이페이지가 읽는다. */
export type DirectoryAssignedDuty = {
  deptName: string | null;
  workCategory: string | null;
  taskDetail: string | null;
  roleScope: string | null;
  cycle: string | null;
  siteScope: string | null;
  coopDept: string | null;
};

/** 명함의 「담당 업무」 모달이 열릴 때만 받는다. */
export function useDirectoryAssignedDuties(personnelId: string, enabled: boolean) {
  return useQuery({
    queryKey: [...queryKeys.directoryDutyAssignments, personnelId],
    queryFn: () => apiFetch<DirectoryAssignedDuty[]>(`/v1/directory/${personnelId}/duty-assignments`),
    enabled,
    staleTime: 60_000,
  });
}

/** 마이페이지 「내 업무분장」 — 인사 정보가 이어지지 않은 계정이면 빈 목록. */
export function useMyAssignedDuties() {
  return useQuery({
    queryKey: queryKeys.myDutyAssignments,
    queryFn: () => apiFetch<DirectoryAssignedDuty[]>('/v1/home/duty-assignments'),
  });
}

/** 서버에 닿지 못했고 개발 중일 때만 빈 트리로 물러선다 — 세션·연차와 같은 판정이다. */
async function fetchTree(): Promise<DirectoryUnit[]> {
  try {
    return await apiFetch<DirectoryUnit[]>('/v1/directory');
  } catch (error) {
    const unreachable =
      error instanceof TypeError || (error instanceof ApiRequestError && error.apiError === null);
    // `env`(Vite 전용) 대신 주입된 런타임 설정을 본다 — 이 파일은 앱(Metro)도 그대로 import 한다.
    if (unreachable && runtimeConfig().mockSessionWhenOffline) return [];
    throw error;
  }
}
